import React, { useState } from "react";
import useDebounce from "@shared/hooks/useDebounce";
import SearchItem from "@ui-kit/data-display/SearchItem/SearchItem.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import {
  SelectedItemCard,
  SearchFieldCounter,
  SelectedItemTags,
  SelectedItemList,
} from "@shared/components/application/form/FormField/SearchFieldShared.web";
import type { SubItemType } from "@shared/components/application/form/FormField/types";
import Icon from "@ui-kit/icons/Icon.web";
import useSearchSharedState from "../search-shared/useSearchSharedState";
import useSearchMembershipGroup from "./hooks/useSearchMembershipGroup";
import type {
  MembershipSearchItemType,
  SearchMembershipSharedProps,
} from "./SearchMembership.props";

const DEFAULT_SUB_ITEMS_KEY = "membership_levels";

export default function SearchMembership({
  single,
  service,
  searchParams,
  fetchOptions,
  onChange,
  onSelect,
  onRemove,
  showSelectedBelow = false,
  withSubItems = false,
  renderSelectedItem,
  counters,
  getFormValue,
  filterOptions,
  subItemsKey = DEFAULT_SUB_ITEMS_KEY,
  selectedItemsDisplay = "cards",
  ...rest
}: SearchMembershipProps): JSX.Element {
  const [inputValue, setInputValue] = useState("");
  const [debouncedInput] = useDebounce(inputValue, 500);

  const useFetchOpts = Boolean(fetchOptions && !service);

  const serviceResult = useSearchMembershipGroup({
    membershipGroupSearchInput: debouncedInput,
    single,
    customParams: (searchParams ?? {}) as Record<string, unknown>,
    service,
    enabled: !useFetchOpts,
  });

  const {
    selectedSet,
    filteredItems,
    counterItems,
    subItemSelections,
    displayMode,
    handleAdd,
    handleRemove,
    handleSingleSelect,
    handleToggleSubItem,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    singleValue: resolvedSingleValue,
  } = useSearchSharedState<MembershipSearchItemType>({
    serviceResult,
    fetchOptions,
    service,
    debouncedQuery: debouncedInput,
    single,
    value: rest.value,
    onChange,
    onSelect,
    onRemove,
    filterOptions,
    withSubItems,
    subItemsKey,
    counters,
    getFormValue,
    selectedItemsDisplay,
    showSelectedBelow,
  });

  return (
    <Flex direction="column" gap="md" width="100%">
      {showSelectedBelow &&
        counterItems.length > 0 &&
        selectedSet.length > 0 && <SearchFieldCounter items={counterItems} />}

      <SearchItem<MembershipSearchItemType>
        {...rest}
        InputLeftElementIcon={<Icon.MagnifyingGlass.Outline color="fg.muted" />}
        single={single}
        multiple={!single}
        items={filteredItems}
        isFetching={isFetching}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        selectedSet={showSelectedBelow ? [] : selectedSet}
        value={single ? resolvedSingleValue : undefined}
        onDropdownClickChange={single ? undefined : handleAdd}
        handleSelectedItemChange={
          single
            ? handleSingleSelect
            : (item) => {
                if (item) handleRemove(item.id);
              }
        }
        onInputValueChange={(change) => setInputValue(change)}
        handleInputValueChange={(change) => {
          setInputValue(change);
        }}
      />

      {showSelectedBelow && selectedSet.length > 0 && (
        <>
          {renderSelectedItem &&
            selectedSet.map((item) => {
              const subItems = item[
                subItemsKey as keyof MembershipSearchItemType
              ] as SubItemType[] | undefined;
              return (
                <React.Fragment key={item.id}>
                  {renderSelectedItem({
                    item,
                    onRemove: () => handleRemove(item.id),
                    subItems,
                    selectedSubItemIds: subItemSelections.get(item.id) ?? [],
                    onToggleSubItem: withSubItems
                      ? (subId, selected) =>
                          handleToggleSubItem(item.id, subId, selected)
                      : undefined,
                    isLoading: false,
                  })}
                </React.Fragment>
              );
            })}

          {!renderSelectedItem && displayMode === "tags" && (
            <SelectedItemTags
              items={selectedSet}
              onRemove={(id) => handleRemove(id)}
            />
          )}

          {!renderSelectedItem && displayMode === "list" && (
            <SelectedItemList
              items={selectedSet}
              onRemove={(id) => handleRemove(id)}
            />
          )}

          {!renderSelectedItem && displayMode === "cards" && (
            <Flex direction="column" gap="md">
              {selectedSet.map((item) => {
                const subItems = item[
                  subItemsKey as keyof MembershipSearchItemType
                ] as SubItemType[] | undefined;
                const selectedSubIds = subItemSelections.get(item.id) ?? [];
                return (
                  <SelectedItemCard
                    key={item.id}
                    title={item.name ?? ""}
                    subItems={withSubItems ? subItems : undefined}
                    selectedSubItemIds={selectedSubIds}
                    onDelete={() => handleRemove(item.id)}
                    onToggleSubItem={
                      withSubItems
                        ? (subId, selected) =>
                            handleToggleSubItem(item.id, subId, selected)
                        : undefined
                    }
                  />
                );
              })}
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
}

export type SearchMembershipProps = SearchMembershipSharedProps & {
  // Web-specific props
};
